import { useState } from 'react';
import type { LoanFinancialSnapshotV1 } from './loan-financial.types';
import { formatMoneyAr, formatDateTimeAr } from './loan-financial.format';
import { MoneyRow, SectionCard } from './LoanUiBits';
import { apiService } from '../../services/api.service';

type Props = {
  loanId: string;
  snap: LoanFinancialSnapshotV1;
  canRestructure: boolean;
  loanStatus?: string;
  products: Array<{ id: string; name: string; interestRate?: number }>;
  onRefresh: () => Promise<void> | void;
};

type Quote = {
  quoteId: string;
  status: string;
  asOf: string;
  totalRefinanced: number;
  principalIncluded: number;
  interestIncluded: number;
  feesIncluded: number;
  penaltiesIncluded: number;
  futureInterestRemoved: number;
  validUntil: string;
  accountingBlocks?: string[];
};

/**
 * Wizard mínimo 4K — no masivo. Requiere permiso refinance_loan vía API.
 */
export function LoanRestructuringPanel({
  loanId,
  snap,
  canRestructure,
  loanStatus,
  products,
  onRefresh,
}: Props) {
  const [productId, setProductId] = useState(products[0]?.id ?? '');
  const [termMonths, setTermMonths] = useState(12);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ targetLoanId: string; loanNumber?: string | null } | null>(
    null,
  );

  const blocked =
    !canRestructure ||
    loanStatus === 'REFINANCED' ||
    loanStatus === 'COMPLETED' ||
    loanStatus === 'CANCELLED' ||
    loanStatus === 'REJECTED' ||
    Boolean(snap.restructuring?.asSource?.status === 'CONFIRMED');

  const asSource = snap.restructuring?.asSource;
  const asTarget = snap.restructuring?.asTarget;

  const requestQuote = async () => {
    if (!productId) {
      setError('Seleccioná un producto');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const q = await apiService.createRestructuringQuote(loanId, {
        productId,
        termMonths,
      });
      setQuote(q);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Error al cotizar');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    if (!quote?.quoteId) return;
    if (quote.accountingBlocks?.length) {
      setError(quote.accountingBlocks.join('; '));
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      const r = await apiService.confirmRestructuringQuote(loanId, { quoteId: quote.quoteId });
      setResult({ targetLoanId: r.targetLoanId, loanNumber: r.loanNumber });
      setQuote(null);
      await onRefresh();
    } catch (e: any) {
      const code = e?.response?.data?.code ?? e?.response?.data?.error?.code;
      if (code === 'RESTRUCTURING_QUOTE_STALE' || e?.response?.status === 409) {
        setError('La cotización quedó desactualizada. Generá una nueva.');
      } else {
        setError(e?.response?.data?.message ?? e?.message ?? 'Error al confirmar');
      }
    } finally {
      setConfirming(false);
    }
  };

  return (
    <SectionCard
      title="Refinanciación"
      subtitle="Nuevo contrato sin desembolso cash. Policy técnica TECHNICAL_DEFAULT_V1 — operación controlada (permiso refinance_loan). No uso masivo sin DEC-10."
    >
      {asSource?.status === 'CONFIRMED' ? (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Este préstamo fue refinanciado.
          {asSource.targetLoanId ? (
            <>
              {' '}
              Nuevo:{' '}
              <a
                className="font-medium underline"
                href={`/loans/${asSource.targetLoanId}`}
              >
                {asSource.targetLoanNumber ?? asSource.targetLoanId}
              </a>{' '}
              · {formatMoneyAr(asSource.totalRefinanced)}
              {asSource.confirmedAt ? ` · ${formatDateTimeAr(asSource.confirmedAt)}` : null}
            </>
          ) : null}
        </div>
      ) : null}

      {asTarget?.status === 'CONFIRMED' ? (
        <div className="mb-3 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          Originado por refinanciación del préstamo{' '}
          <a className="font-medium underline" href={`/loans/${asTarget.sourceLoanId}`}>
            {asTarget.sourceLoanNumber ?? asTarget.sourceLoanId}
          </a>
          .
        </div>
      ) : null}

      {blocked ? (
        <p className="text-sm text-slate-600">Refinanciación no disponible en el estado actual.</p>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Producto</span>
              <select
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              >
                <option value="">—</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Plazo (meses)</span>
              <input
                type="number"
                min={1}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                value={termMonths}
                onChange={(e) => setTermMonths(Number(e.target.value) || 1)}
              />
            </label>
          </div>

          <button
            type="button"
            disabled={loading || !productId}
            onClick={() => void requestQuote()}
            className="rounded bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {loading ? 'Cotizando…' : 'Cotizar refinanciación'}
          </button>

          {quote ? (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
              <MoneyRow label="Capital incluido" value={quote.principalIncluded} />
              <MoneyRow label="Interés incluido" value={quote.interestIncluded} />
              <MoneyRow label="Fees incluidos" value={quote.feesIncluded} />
              <MoneyRow label="Punitorios incluidos" value={quote.penaltiesIncluded} />
              <MoneyRow label="Interés futuro removido" value={quote.futureInterestRemoved} />
              <MoneyRow label="Total refinanciado" value={quote.totalRefinanced} emphasis />
              <p className="mt-2 text-xs text-slate-500">
                Válida hasta {formatDateTimeAr(quote.validUntil)} · asOf {quote.asOf}
              </p>
              {quote.accountingBlocks?.length ? (
                <p className="mt-2 text-xs text-red-600">{quote.accountingBlocks.join('; ')}</p>
              ) : (
                <button
                  type="button"
                  disabled={confirming}
                  onClick={() => void confirm()}
                  className="mt-3 rounded bg-emerald-700 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  {confirming ? 'Confirmando…' : 'Confirmar refinanciación'}
                </button>
              )}
            </div>
          ) : null}

          {result ? (
            <p className="text-sm text-emerald-800">
              Creado{' '}
              <a className="underline" href={`/loans/${result.targetLoanId}`}>
                {result.loanNumber ?? result.targetLoanId}
              </a>
            </p>
          ) : null}
        </div>
      )}

      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </SectionCard>
  );
}
